import { useState } from "react";
import { Phone, Check, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackFormSubmit } from "@/lib/analytics";
import { API } from "@/lib/api";

const CLINIC_TYPES = [
  { value: "medicina_estetica", label: "Medicina estética" },
  { value: "cirugia_estetica", label: "Cirugía estética" },
  { value: "dermatologia", label: "Dermatología" },
  { value: "clinica_dental", label: "Clínica dental" },
  { value: "centro_bienestar", label: "Centro de bienestar" },
  { value: "otro", label: "Otro" },
];

export function ContactoRapidoForm({ onOpenPrivacy }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    nombre: "", email: "", telefono: "", tipo_clinica: "", acepta_lopd: false,
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.telefono || !form.tipo_clinica) {
      const missing = [];
      if (!form.nombre) missing.push("Nombre");
      if (!form.email) missing.push("Email");
      if (!form.telefono) missing.push("Móvil");
      if (!form.tipo_clinica) missing.push("Tipo de clínica");
      toast.error(`Faltan campos obligatorios: ${missing.join(", ")}`);
      return;
    }
    if (!form.acepta_lopd) {
      toast.error("Debes aceptar la política de privacidad");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/contacto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      let data;
      try { data = await response.json(); } catch { data = {}; }
      if (!response.ok) {
        toast.error(data.detail || `Error del servidor (${response.status})`);
        return;
      }
      setSuccess(true);
      setForm({ nombre: "", email: "", telefono: "", tipo_clinica: "", acepta_lopd: false });
      trackFormSubmit("contacto");
      toast.success("¡Recibido! En breve nos ponemos en contacto contigo.");
    } catch (error) {
      toast.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto mb-16">
      <div className="text-center mb-8">
        <span className="eyebrow mb-4 inline-flex" style={{ background: 'rgba(59, 130, 246, 0.15)', borderColor: 'rgba(59, 130, 246, 0.3)', color: '#60A5FA' }}>
          <Phone className="w-4 h-4" />
          <span>Contacto rápido</span>
        </span>
        <h2 className="section-title mt-4 text-2xl">¿Muchos campos para rellenar?</h2>
        <p className="text-gray-400 mt-2">Solo quieres que nos pongamos en contacto contigo. Rellena esto:</p>
      </div>

      {success ? (
        <div className="form-container p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¡Recibido!</h3>
          <p className="text-gray-400">En breve nos ponemos en contacto contigo.</p>
          <Button onClick={() => setSuccess(false)} variant="outline" className="btn-secondary mt-6">
            Enviar otro mensaje
          </Button>
        </div>
      ) : (
        <div className="form-container">
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-4">
              <div>
                <label className="form-label">Nombre *</label>
                <Input type="text" value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} placeholder="Tu nombre" className="form-input" required data-testid="contacto-nombre" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="tu@email.com" className="form-input" required data-testid="contacto-email" />
              </div>
              <div>
                <label className="form-label">Móvil *</label>
                <Input type="tel" value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} placeholder="+34 600 000 000" className="form-input" required data-testid="contacto-telefono" />
              </div>
              <div>
                <label className="form-label">Tipo de clínica *</label>
                <Select value={form.tipo_clinica} onValueChange={(v) => handleChange('tipo_clinica', v)}>
                  <SelectTrigger className="form-input" data-testid="contacto-tipo-clinica">
                    <SelectValue placeholder="Selecciona tipo de clínica" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLINIC_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 rounded-lg border border-gray-700 bg-gray-800/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.acepta_lopd} onCheckedChange={(checked) => handleChange('acepta_lopd', checked)} className="mt-1" data-testid="contacto-lopd" />
                  <span className="text-gray-300 text-sm leading-relaxed">
                    Acepto la <button type="button" onClick={onOpenPrivacy} className="text-blue-400 underline">política de privacidad</button> y el tratamiento de mis datos para que contacten conmigo. *
                  </span>
                </label>
              </div>

              <Button type="submit" className="btn-primary w-full py-3" disabled={loading} data-testid="contacto-submit">
                {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Enviando...</>) : (<><Send className="w-4 h-4" />Contactar</>)}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
